import type { RailFolder, RailItem } from "@atrium/shared";

/*
 * Pure operations on a rail layout (top-level items, each a community or a folder
 * of communities). Indices are positions in the container *before* the moving
 * item is taken out, which is what a drop indicator points at.
 */

export type Container = string | null; // folder id, or null for the top level

export interface Location {
  folderId: Container;
  index: number;
}

export function newFolderId() {
  return `f${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

/** Drop empty folders. */
const tidy = (items: RailItem[]) => items.filter((it) => it.kind === "community" || it.communityIds.length > 0);

export function locate(items: RailItem[], communityId: string): Location | null {
  for (const [i, it] of items.entries()) {
    if (it.kind === "community" && it.id === communityId) return { folderId: null, index: i };
    if (it.kind === "folder") {
      const j = it.communityIds.indexOf(communityId);
      if (j >= 0) return { folderId: it.id, index: j };
    }
  }
  return null;
}

export const folderOf = (items: RailItem[], communityId: string) => {
  const loc = locate(items, communityId);
  return loc?.folderId ? (items.find((i) => i.kind === "folder" && i.id === loc.folderId) as RailFolder) : null;
};

export const topIndex = (items: RailItem[], id: string) => items.findIndex((it) => it.id === id);

/** Move a community to a place in the top level or in a folder. */
export function moveCommunity(items: RailItem[], communityId: string, to: Location): RailItem[] {
  const from = locate(items, communityId);
  if (!from) return items;
  let index = to.index;
  if (from.folderId === to.folderId && from.index < index) index -= 1;
  // Taking it out of a folder leaves the (maybe empty) folder in place, so top-level indices stay valid until tidy().
  let next: RailItem[] =
    from.folderId === null
      ? items.filter((_, i) => i !== from.index)
      : items.map((it) => (it.kind === "folder" && it.id === from.folderId ? { ...it, communityIds: it.communityIds.filter((c) => c !== communityId) } : it));
  if (to.folderId === null) {
    next.splice(Math.max(0, Math.min(index, next.length)), 0, { kind: "community", id: communityId });
  } else {
    next = next.map((it) => {
      if (it.kind !== "folder" || it.id !== to.folderId) return it;
      const ids = [...it.communityIds];
      ids.splice(Math.max(0, Math.min(index, ids.length)), 0, communityId);
      return { ...it, communityIds: ids };
    });
  }
  return tidy(next);
}

/** Move a top-level item (community or folder) to a top-level index. */
export function moveTop(items: RailItem[], id: string, index: number): RailItem[] {
  const from = topIndex(items, id);
  if (from < 0) return items;
  const next = [...items];
  const [it] = next.splice(from, 1);
  next.splice(Math.max(0, Math.min(from < index ? index - 1 : index, next.length)), 0, it);
  return next;
}

/** Drop a community onto another one: both go into a new folder where the target was. */
export function combine(items: RailItem[], sourceId: string, targetId: string): { items: RailItem[]; folderId: string | null } {
  if (sourceId === targetId) return { items, folderId: null };
  const target = locate(items, targetId);
  if (!target) return { items, folderId: null };
  if (target.folderId) return { items: moveCommunity(items, sourceId, { folderId: target.folderId, index: target.index + 1 }), folderId: target.folderId };
  const id = newFolderId();
  const without = moveCommunity(items, sourceId, { folderId: null, index: items.length }); // out of any folder first
  const next = without.filter((it) => !(it.kind === "community" && it.id === sourceId));
  const at = next.findIndex((it) => it.kind === "community" && it.id === targetId);
  next.splice(at, 1, { kind: "folder", id, name: "", color: null, icon: null, communityIds: [targetId, sourceId] });
  return { items: next, folderId: id };
}

/** Put a community into a new folder of its own, where it is now. */
export function createFolderWith(items: RailItem[], communityId: string): { items: RailItem[]; folderId: string } {
  const id = newFolderId();
  const loc = locate(items, communityId);
  const folder: RailItem = { kind: "folder", id, name: "", color: null, icon: null, communityIds: [communityId] };
  if (!loc) return { items: [...items, folder], folderId: id };
  if (loc.folderId === null) return { items: items.map((it, i) => (i === loc.index ? folder : it)), folderId: id };
  // Already in a folder: the new folder goes right after the old one.
  const after = topIndex(items, loc.folderId) + 1;
  const next = moveCommunity(items, communityId, { folderId: null, index: after });
  return { items: next.map((it) => (it.kind === "community" && it.id === communityId ? folder : it)), folderId: id };
}

/** Take a community out of its folder; it lands right after the folder. */
export function removeFromFolder(items: RailItem[], communityId: string): RailItem[] {
  const loc = locate(items, communityId);
  if (!loc?.folderId) return items;
  return moveCommunity(items, communityId, { folderId: null, index: topIndex(items, loc.folderId) + 1 });
}

/** Replace a folder with its communities, in order. */
export function ungroup(items: RailItem[], folderId: string): RailItem[] {
  return items.flatMap((it) => (it.kind === "folder" && it.id === folderId ? it.communityIds.map((id): RailItem => ({ kind: "community", id })) : [it]));
}

export function updateFolder(items: RailItem[], folderId: string, patch: Partial<Pick<RailFolder, "name" | "color" | "icon">>): RailItem[] {
  return items.map((it) => (it.kind === "folder" && it.id === folderId ? { ...it, ...patch } : it));
}

/** One keyboard step (Alt+↑/↓) for a community: within its container, stepping out of a folder at either end. */
export function stepCommunity(items: RailItem[], communityId: string, dir: -1 | 1): RailItem[] {
  const loc = locate(items, communityId);
  if (!loc) return items;
  if (loc.folderId === null) {
    const to = dir < 0 ? loc.index - 1 : loc.index + 2;
    if (to < 0 || to > items.length) return items;
    return moveCommunity(items, communityId, { folderId: null, index: to });
  }
  const folder = items.find((i) => i.id === loc.folderId) as RailFolder;
  const fIdx = topIndex(items, folder.id);
  if (dir < 0) return loc.index > 0 ? moveCommunity(items, communityId, { folderId: folder.id, index: loc.index - 1 }) : moveCommunity(items, communityId, { folderId: null, index: fIdx });
  return loc.index < folder.communityIds.length - 1
    ? moveCommunity(items, communityId, { folderId: folder.id, index: loc.index + 2 })
    : moveCommunity(items, communityId, { folderId: null, index: fIdx + 1 });
}

export function stepTop(items: RailItem[], id: string, dir: -1 | 1): RailItem[] {
  const i = topIndex(items, id);
  const to = dir < 0 ? i - 1 : i + 2;
  if (i < 0 || to < 0 || to > items.length) return items;
  return moveTop(items, id, to);
}

export const sameLayout = (a: RailItem[], b: RailItem[]) => JSON.stringify(a) === JSON.stringify(b);
